import { _ as _ts_decorate } from "@swc/helpers/_/_ts_decorate";
import { _ as _ts_metadata } from "@swc/helpers/_/_ts_metadata";
import { computed, flow, observable, runInAction } from "mobx";
import { EProductPlanPaymentInterval, ESubscriptionCancellationReason, ESubscriptionStatus, routeLocationLicensePost, routeLocationSubscriptionChangePlanGet, routeLocationSubscriptionChangePlanPatch, routeLocationSubscriptionsDelete } from "@devowl-wp/api-real-commerce";
import { License } from "./license.js";
import { RealCommerceError } from "../../../exceptions/realCommerceError.js";
import { localizePaymentInterval } from "../../../others/localizePaymentInterval.js";
import { formatAmount } from "../../../utils/formatAmount.js";
import { formatLocaleDate } from "../../../utils/formatLocaleDate.js";
import { formatNumber } from "../../../utils/getDecimalSeparator.js";
import { getLegalManufacturerUrls, getLegalSellerUrls } from "../../../utils/legalEntities.js";
import { realCommerceRequest } from "../../../utils/realCommerceRequest.js";
class Subscription {
    get isOneTime() {
        return this.data.productPlan.paymentInterval === EProductPlanPaymentInterval.OneTime;
    }
    get humanReadablePaymentMethod() {
        return this.getHumanReadablePaymentMethod();
    }
    get canBeModified() {
        return !!this.data.ecommerceSystemSubscriptionId;
    }
    // Lifetime is the active subset of OneTime: recurring plans always carry
    // a `nextInvoicing` while active, and `nextInvoicing` is only nulled when
    // the subscription transitions out of Active (CancellationPending, Revoked).
    get isLifetime() {
        return this.isOneTime && this.isActive;
    }
    get isPastDue() {
        return this.data.paymentInformation?.status === "past_due";
    }
    // Past-due currently is the only state that locks down mutations;
    // wrapper so future read-only states (e.g. paused) don't need a sweep.
    get isReadOnly() {
        return this.isPastDue;
    }
    get isActive() {
        return this.data.status === ESubscriptionStatus.Active;
    }
    get isCancelled() {
        return this.data.status === ESubscriptionStatus.Canceled;
    }
    get isCancellationPending() {
        return this.data.status === ESubscriptionStatus.CancellationPending;
    }
    get isExpired() {
        return this.isCancelled || this.isCancellationPending && this.data.cancellationReason === ESubscriptionCancellationReason.PaymentFailed;
    }
    get hasEligibleUpgrade() {
        return this.changePlan.some((option)=>option.type === "upgrade");
    }
    // Legacy checkouts (pre-customerType field) carry `null`; fall back to the
    // stricter consumer flow so unknown buyers see legal-protection-grade UI.
    get customerType() {
        return this.data.checkout?.customerType === "company" ? "company" : "individual";
    }
    get isReverseCharge() {
        return this.data.isReverseCharge ?? false;
    }
    // Net/gross treatment of displayed amounts: "net" for reverse-charge B2B (Paddle bills net), else "gross" (incl.
    // VAT). Pass to `formatAmount({ taxTreatment })`; the VAT amount itself is not shown in the customer center.
    get taxTreatment() {
        return this.isReverseCharge ? "net" : "gross";
    }
    get contractOrCustomerOrderNumberLabel() {
        return {
            key: this.canBeModified ? "Contract" : "Order",
            id: this.data.uuid
        };
    }
    get nextRenewalDateLabel() {
        if (this.isLifetime || this.isOneTime || this.isCancelled || this.isCancellationPending) {
            return "";
        }
        if (!this.data.nextInvoicing) {
            return "";
        }
        return formatLocaleDate(this.data.nextInvoicing);
    }
    /**
     * Status-aware primary date label for the always-visible card header.
     * Avoids the "Valid until = Renews" duplicate by picking the single most
     * relevant date for the subscription's current state.
     *  - Lifetime  → "Lifetime" (no date)
     *  - Active recurring → "Renews <nextInvoicing>"
     *  - One-time → "Valid until <validUntil>"
     *  - Cancelled / cancellation pending → "Access ends on <validUntil>"
     */ get primaryDateLabel() {
        if (this.isLifetime) {
            return {
                kind: "lifetime"
            };
        }
        if (this.isCancelled || this.isCancellationPending) {
            return {
                kind: "access-ends",
                date: this.data.validUntil
            };
        }
        if (this.isOneTime) {
            return {
                kind: "valid",
                date: this.data.validUntil
            };
        }
        return {
            kind: "renews",
            date: this.data.nextInvoicing
        };
    }
    get installations() {
        return this.store?.installations.get(this.data.id);
    }
    get productVariants() {
        return this.store?.rpmProductVariantToSubscription.get(this.data.id);
    }
    get licenses() {
        return this.store ? Array.from(this.store.licenses.values()).filter((l)=>l.data.subscription.id === this.data.id) : [];
    }
    get labelPrice() {
        return formatNumber(+this.data.productPlan.price[0].recurringPrice);
    }
    get labelPaymentInterval() {
        return localizePaymentInterval(this.data.productPlan.paymentInterval);
    }
    // Contract price (ongoing recurring rate) with net/gross qualifier; `undefined` on a legacy back-fill miss.
    // Intentionally no fallback — the divergent display needs the contract and next charge as separate values.
    // Consumers that just want a single price to show use `currentRecurringPriceLabel`, which owns the fallback.
    get contractPriceLabel() {
        const { contractRecurringPrice } = this.data;
        if (contractRecurringPrice === null || contractRecurringPrice === undefined) {
            return undefined;
        }
        return formatAmount({
            amount: +contractRecurringPrice,
            currency: this.data.currency,
            paymentInterval: this.data.productPlan.paymentInterval,
            taxTreatment: this.taxTreatment
        });
    }
    // The single upcoming charge with net/gross qualifier — no interval suffix, because it is one charge (a discounted
    // next renewal), not the ongoing rate. `undefined` when Paddle has no scheduled next payment.
    get nextBillingChargeLabel() {
        const { nextBillingAmount } = this.data;
        if (nextBillingAmount === null || nextBillingAmount === undefined) {
            return undefined;
        }
        return formatAmount({
            amount: +nextBillingAmount,
            currency: this.data.currency,
            taxTreatment: this.taxTreatment
        });
    }
    // Both prices known and differing — drives the dual-price header.
    get hasDivergentNextBilling() {
        const { contractRecurringPrice, nextBillingAmount } = this.data;
        if (contractRecurringPrice === null || contractRecurringPrice === undefined || nextBillingAmount === null || nextBillingAmount === undefined) {
            return false;
        }
        return +contractRecurringPrice !== +nextBillingAmount;
    }
    // Upcoming renewal is cheaper than the contract price — a discount already booked for the next term.
    get hasNextRenewalDiscount() {
        const { contractRecurringPrice, nextBillingAmount } = this.data;
        if (contractRecurringPrice === null || contractRecurringPrice === undefined || nextBillingAmount === null || nextBillingAmount === undefined) {
            return false;
        }
        return +nextBillingAmount < +contractRecurringPrice;
    }
    // The single recurring price for non-divergent surfaces, and the one place the fallback lives: the contract price,
    // else the upcoming charge shown as the ongoing rate for legacy rows without a snapshot. Carries the net/gross
    // qualifier. `undefined` for one-time / lifetime (caller uses the plan list price).
    get currentRecurringPriceLabel() {
        const { contractRecurringPrice, nextBillingAmount } = this.data;
        const amount = contractRecurringPrice !== null && contractRecurringPrice !== undefined ? +contractRecurringPrice : nextBillingAmount !== null && nextBillingAmount !== undefined ? +nextBillingAmount : undefined;
        if (amount === undefined) {
            return undefined;
        }
        return formatAmount({
            amount,
            currency: this.data.currency,
            paymentInterval: this.data.productPlan.paymentInterval,
            taxTreatment: this.taxTreatment
        });
    }
    get seller() {
        return getLegalSellerUrls();
    }
    get manufacturer() {
        return getLegalManufacturerUrls();
    }
    constructor(data, store){
        this.isBusy = false;
        /**
     * Save the last applied coupon of this transaction. This is necessary so
     * we can automatically apply the coupon when changing the plan.
     */ this.lastAppliedCoupon = "";
        this.changePlan = [];
        this.cancel = flow(function*({ cancellationReason }) {
            this.isBusy = true;
            yield this.store?.probablyRenewJwt();
            try {
                yield realCommerceRequest({
                    location: routeLocationSubscriptionsDelete,
                    request: {
                        subscription: {
                            id: this.data.id,
                            cancellationReason
                        }
                    },
                    settings: {
                        headers: this.store?.authorizationHeader
                    }
                });
                yield this.store?.store.fetchSubscriptionsAndLicenses();
            } catch (e) {
                console.log(e);
                throw new RealCommerceError(e);
            } finally{
                this.isBusy = false;
            }
        });
        this.fetchChangePlans = flow(function*(coupon) {
            this.isBusy = true;
            yield this.store?.probablyRenewJwt();
            try {
                const result = yield realCommerceRequest({
                    location: routeLocationSubscriptionChangePlanGet,
                    params: {
                        subscriptionId: this.data.id,
                        coupon
                    },
                    settings: {
                        headers: this.store?.authorizationHeader
                    }
                });
                this.changePlan = result.options;
                this.lastAppliedCoupon = coupon ?? "";
                this.lastFetchPlanError = undefined;
            } catch (e) {
                console.log(e);
                this.lastFetchPlanError = new RealCommerceError(e);
                throw this.lastFetchPlanError;
            } finally{
                this.isBusy = false;
            }
        });
        this.applyChangePlan = flow(function*(productPlanId) {
            const selectedPlan = this.changePlan.find((option)=>option.productPlan.id === productPlanId);
            if (!selectedPlan) {
                throw new Error("applyChangePlan: selected productPlanId not found in changePlan options");
            }
            this.isBusy = true;
            yield this.store?.probablyRenewJwt();
            try {
                yield realCommerceRequest({
                    location: routeLocationSubscriptionChangePlanPatch,
                    request: {
                        subscription: {
                            id: this.data.id
                        },
                        checkout: {
                            isTermsAccepted: true,
                            coupon: selectedPlan.discount ? this.lastAppliedCoupon || undefined : undefined,
                            productPlan: {
                                id: productPlanId
                            }
                        }
                    },
                    settings: {
                        headers: this.store?.authorizationHeader
                    }
                });
                this.lastAppliedCoupon = "";
                yield this.store?.store.fetchSubscriptionsAndLicenses();
            } catch (e) {
                console.log(e);
                throw new RealCommerceError(e);
            } finally{
                this.isBusy = false;
            }
        });
        this.addLicense = flow(function*({ rpmProductVariantId, ...rest }) {
            yield this.store?.probablyRenewJwt();
            try {
                const { license } = yield realCommerceRequest({
                    location: routeLocationLicensePost,
                    request: {
                        subscription: {
                            id: this.data.id
                        },
                        productPlanRpmProductVariant: {
                            rpmProductVariantId
                        },
                        license: rest
                    },
                    settings: {
                        headers: this.store?.authorizationHeader
                    }
                });
                let created;
                if (this.store) {
                    created = new License(license, this.store);
                    this.store.licenses.set(license.id, created);
                }
                yield this.store?.store.fetchSubscriptionsAndLicenses();
                // Re-resolve from the session map after the refetch so the caller
                // gets the same instance that the SubscriptionDetailBody list renders —
                // otherwise the flash modifier on the card wouldn't match.
                return this.store?.licenses.get(license.id) ?? created;
            } catch (e) {
                console.log(e);
                throw new RealCommerceError(e);
            }
        });
        this.store = store;
        runInAction(()=>{
            this.data = data;
        });
    }
    editLicense(license, input) {
        return license.updateDetails(input);
    }
    deleteLicense(license) {
        return license.delete();
    }
}
_ts_decorate([
    observable
], Subscription.prototype, "isBusy", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Number)
], Subscription.prototype, "sortIndex", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], Subscription.prototype, "data", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof RealCommerceError === "undefined" ? Object : RealCommerceError)
], Subscription.prototype, "lastFetchPlanError", void 0);
_ts_decorate([
    observable
], Subscription.prototype, "lastAppliedCoupon", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Array)
], Subscription.prototype, "changePlan", void 0);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "isOneTime", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "humanReadablePaymentMethod", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "canBeModified", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "isLifetime", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "isPastDue", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "isReadOnly", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "isActive", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "isCancelled", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "isCancellationPending", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "isExpired", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "hasEligibleUpgrade", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "customerType", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", Boolean),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "isReverseCharge", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", String),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "taxTreatment", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", typeof ContractOrCustomerOrderLabel === "undefined" ? Object : ContractOrCustomerOrderLabel),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "contractOrCustomerOrderNumberLabel", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", String),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "nextRenewalDateLabel", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", Object),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "primaryDateLabel", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "installations", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "productVariants", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "licenses", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "labelPrice", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "labelPaymentInterval", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", Object),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "contractPriceLabel", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", Object),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "nextBillingChargeLabel", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", Boolean),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "hasDivergentNextBilling", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", Boolean),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "hasNextRenewalDiscount", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", Object),
    _ts_metadata("design:paramtypes", [])
], Subscription.prototype, "currentRecurringPriceLabel", null);
export { Subscription };
